import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Observation, QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Paper, Stack, Text, Title } from "@mantine/core";

import { laborObservationVersionDiffRowHasVisibleChange } from "@/encounters/components/EncounterLabor/utils/labor-observation-version-diff-change";
import {
  collectLaborStageMemberRowsForVersionDiff,
  getLaborFlowsheetObservationDisplayName,
} from "@/encounters/components/EncounterLabor/utils/labor-stage-version-diff";
import { shouldIncludeLaborStageMemberRowInVersionDiff } from "@/encounters/components/EncounterLabor/utils/labor-stage-version-diff-inclusion";
import { resolveObservationAmendPredecessor } from "@/encounters/components/EncounterLabor/utils/observation-version-chain";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";

import {
  collapseObservationRowsToLatestChain,
  ObservationVersionRow,
} from "./collapse-observation-version-chains";
import LaborObservationDiff from "./LaborObservationDiff";

interface LaborStageDiffProps {
  stageCode: string;
  stageDisplay: string;
  laborStageObservationsForDiff: Observation[];
  questionnaireResponsesById?: Map<string, QuestionnaireResponse>;
  isLaborDiffBundleLoading?: boolean;
  previousEncounterQuestionnaireResponse?: QuestionnaireResponse;
  currentEncounterQuestionnaireResponse?: QuestionnaireResponse;
  /** When true, only flowsheet rows that belong to this encounter amend pair are shown. */
  restrictToEncounterAmendmentWindow?: boolean;
  /** When true, only rows with an ObservationPreviousVersion link are shown (QR pair unavailable). */
  showOnlyObservationVersionChainDiffs?: boolean;
  provenanceTargetObservationIds?: ReadonlySet<string>;
  provenanceTargetFlowsheetQuestionnaireResponseIds?: ReadonlySet<string>;
  stageMembershipChanges?: LaborAmendStageMembershipChangeRecord[];
  provenanceObservationsById?: ReadonlyMap<string, Observation>;
}

function resolveMemberDisplayTitle(
  change: LaborAmendStageMembershipChangeRecord,
  provenanceObservationsById: ReadonlyMap<string, Observation> | undefined,
): string {
  if (change.memberDisplayTitle) {
    return change.memberDisplayTitle;
  }
  const observation = provenanceObservationsById?.get(change.memberObservationId);
  return getLaborFlowsheetObservationDisplayName(observation?.code?.coding?.[0]?.code);
}

/**
 * Resolves current/previous observation pair for a stage move row (note edits + stage moves).
 */
function resolveStageMoveMemberObservationDiffPair(
  memberObservationId: string,
  memberRows: ObservationVersionRow[],
  observationsById: ReadonlyMap<string, Observation>,
  allObservations: ReadonlyArray<Observation>,
): ObservationVersionRow | undefined {
  const memberRow = memberRows.find((row) => row.currentObservation.id === memberObservationId);
  const currentObservation =
    memberRow?.currentObservation ?? observationsById.get(memberObservationId);

  if (!currentObservation) {
    return undefined;
  }

  return {
    currentObservation,
    previousObservation:
      memberRow?.previousObservation ??
      resolveObservationAmendPredecessor(currentObservation, observationsById, allObservations),
  };
}

const LaborStageDiff = ({
  stageCode,
  stageDisplay,
  laborStageObservationsForDiff,
  questionnaireResponsesById,
  isLaborDiffBundleLoading = false,
  previousEncounterQuestionnaireResponse,
  currentEncounterQuestionnaireResponse,
  restrictToEncounterAmendmentWindow = false,
  showOnlyObservationVersionChainDiffs = false,
  provenanceTargetObservationIds,
  provenanceTargetFlowsheetQuestionnaireResponseIds,
  stageMembershipChanges = [],
  provenanceObservationsById,
}: LaborStageDiffProps) => {
  const { encounterId, clientId } = useParams<{ encounterId: string; clientId: string }>();
  const medplum = useMedplum();

  const [isFetchingMemberRows, setIsFetchingMemberRows] = useState(true);
  const [memberRows, setMemberRows] = useState<ObservationVersionRow[]>([]);

  const removals = useMemo(
    () => stageMembershipChanges.filter((change) => change.fromStageLoincCode === stageCode),
    [stageMembershipChanges, stageCode],
  );

  const additions = useMemo(
    () => stageMembershipChanges.filter((change) => change.toStageLoincCode === stageCode),
    [stageMembershipChanges, stageCode],
  );

  const stageMoveMemberIds = useMemo(
    () =>
      new Set(
        [...removals, ...additions].map((change) => change.memberObservationId).filter(Boolean),
      ),
    [additions, removals],
  );

  useEffect(() => {
    const loadMemberRows = async () => {
      setIsFetchingMemberRows(true);
      try {
        if (
          isLaborDiffBundleLoading ||
          !encounterId ||
          !clientId ||
          !stageCode ||
          laborStageObservationsForDiff.length === 0
        ) {
          setMemberRows([]);
          return;
        }

        const stageObservation = laborStageObservationsForDiff.find(
          (observation) => observation.valueCodeableConcept?.coding?.[0]?.code === stageCode,
        );

        if (!stageObservation) {
          setMemberRows([]);
          return;
        }

        const rows = await collectLaborStageMemberRowsForVersionDiff(
          medplum,
          stageObservation,
          laborStageObservationsForDiff,
          clientId,
          encounterId,
        );
        setMemberRows(rows);
      } finally {
        setIsFetchingMemberRows(false);
      }
    };
    void loadMemberRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Re-run when encounter/stage/bundle inputs change; Medplum client reference is not a stable effect dependency.
  }, [encounterId, clientId, stageCode, isLaborDiffBundleLoading, laborStageObservationsForDiff]);

  const stageObservation = useMemo(
    () =>
      laborStageObservationsForDiff.find(
        (observation) => observation.valueCodeableConcept?.coding?.[0]?.code === stageCode,
      ),
    [laborStageObservationsForDiff, stageCode],
  );

  const observationsById = useMemo(
    () =>
      new Map(
        laborStageObservationsForDiff
          .filter((observation): observation is Observation & { id: string } =>
            Boolean(observation.id),
          )
          .map((observation) => [observation.id, observation]),
      ),
    [laborStageObservationsForDiff],
  );

  const rowsForDiff = useMemo(() => {
    const observationsForAmendView = memberRows.filter(({ currentObservation }) =>
      shouldIncludeLaborStageMemberRowInVersionDiff(currentObservation, {
        stageMoveMemberIds,
        provenanceTargetObservationIds,
        provenanceTargetFlowsheetQuestionnaireResponseIds,
        restrictToEncounterAmendmentWindow,
        showOnlyObservationVersionChainDiffs,
        previousEncounterQuestionnaireResponse,
        currentEncounterQuestionnaireResponse,
        observationsById,
        questionnaireResponsesById,
      }),
    );

    return collapseObservationRowsToLatestChain(observationsForAmendView, observationsById).filter(
      ({ currentObservation, previousObservation }) =>
        laborObservationVersionDiffRowHasVisibleChange(currentObservation, previousObservation),
    );
  }, [
    memberRows,
    observationsById,
    stageMoveMemberIds,
    provenanceTargetObservationIds,
    provenanceTargetFlowsheetQuestionnaireResponseIds,
    showOnlyObservationVersionChainDiffs,
    restrictToEncounterAmendmentWindow,
    previousEncounterQuestionnaireResponse,
    currentEncounterQuestionnaireResponse,
    questionnaireResponsesById,
  ]);

  if (isLaborDiffBundleLoading || isFetchingMemberRows) return <Loading />;

  const hasMembershipChanges = removals.length > 0 || additions.length > 0;
  const hasObservationDiffs = rowsForDiff.length > 0;

  if (!stageObservation && !hasMembershipChanges) {
    return null;
  }

  if (!hasMembershipChanges && !hasObservationDiffs) {
    return null;
  }

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>{stageDisplay}</Title>
      </Stack>

      {removals.map((change, index) => (
        <Paper
          p="md"
          withBorder
          shadow="xs"
          radius="md"
          key={getStageMembershipChangeCardKey(change, "removed", index)}
        >
          <Stack gap={4}>
            <Text c="red" fw={600} size="sm">
              Moved out of this stage
            </Text>
            <Text size="sm">{resolveMemberDisplayTitle(change, provenanceObservationsById)}</Text>
            {change.toStageDisplay || change.toStageLoincCode ? (
              <Text size="sm" c="dimmed">
                Now in {change.toStageDisplay ?? change.toStageLoincCode}
              </Text>
            ) : null}
          </Stack>
        </Paper>
      ))}

      {additions.map((change, index) => {
        const stageMoveDiffPair = change.memberObservationId
          ? resolveStageMoveMemberObservationDiffPair(
              change.memberObservationId,
              memberRows,
              observationsById,
              laborStageObservationsForDiff,
            )
          : undefined;

        return (
          <Paper
            p="md"
            withBorder
            shadow="xs"
            radius="md"
            key={getStageMembershipChangeCardKey(change, "added", index)}
          >
            <Stack gap="sm">
              <Stack gap={4}>
                <Text c="teal" fw={600} size="sm">
                  Moved into this stage
                </Text>
                <Text size="sm">
                  {resolveMemberDisplayTitle(change, provenanceObservationsById)}
                </Text>
                {change.fromStageDisplay || change.fromStageLoincCode ? (
                  <Text size="sm" c="dimmed">
                    Previously in {change.fromStageDisplay ?? change.fromStageLoincCode}
                  </Text>
                ) : null}
              </Stack>
              {stageMoveDiffPair ? (
                <LaborObservationDiff
                  currentObservation={stageMoveDiffPair.currentObservation}
                  previousObservation={stageMoveDiffPair.previousObservation}
                  title={resolveMemberDisplayTitle(change, provenanceObservationsById)}
                />
              ) : null}
            </Stack>
          </Paper>
        );
      })}

      {rowsForDiff.map(({ currentObservation, previousObservation }) => (
        <LaborObservationDiff
          key={currentObservation.id}
          currentObservation={currentObservation}
          previousObservation={previousObservation}
          title={getLaborFlowsheetObservationDisplayName(
            currentObservation.code?.coding?.[0]?.code,
          )}
        />
      ))}
    </Paper>
  );
};

/**
 * Builds a stable React key for stage membership change cards.
 *
 * @param change - Stage membership move record from amend Provenance.
 * @param variant - Whether the card represents a removal from or addition to this stage.
 * @param index - List index fallback when memberObservationId is missing at runtime.
 */
function getStageMembershipChangeCardKey(
  change: LaborAmendStageMembershipChangeRecord,
  variant: "removed" | "added",
  index: number,
): string {
  const memberObservationId = change.memberObservationId?.trim();
  if (memberObservationId) {
    return `${variant}-${memberObservationId}-${change.fromStageLoincCode ?? ""}-${change.toStageLoincCode}`;
  }

  return `${variant}-${index}-${change.fromStageLoincCode ?? "from"}-${change.toStageLoincCode}`;
}

export default LaborStageDiff;
